import axios from "axios";
import momentTimezone from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { BaseURL } from "../resources/utils/helper";
import { signOutRequest, updateJWTTokens } from "../store/auth/authSlice";
import RenderToast from "../components/atoms/RenderToast";

const useAxios = () => {
  const dispatch = useDispatch();
  const { accessToken, refreshToken } = useSelector(
    (state) => state.authReducer
  );

  // Function to refresh the access token
  const refreshAccessToken = async () => {
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(BaseURL("auth/refresh-token"), {
        token: refreshToken,
      });

      const data = response?.data;
      if (data?.accessToken) {
        dispatch(
          updateJWTTokens({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || refreshToken,
          })
        );
        return data.accessToken;
      }
      return null;
    } catch (error) {
      // If refresh token fails, log out the user
      handleLogout();
      return null;
    }
  };

  const handleLogout = () => {
    dispatch(signOutRequest());
    // Use window.location for navigation instead of useNavigate
    window.location.href = "/";
  };

  const getErrorMsg = (error = null) => {
    if (error?.message === "Network Error") {
      return `Network Error: Please check your internet connection`;
    }
    console.log("error?.response?.data", error?.response?.data);

    const message =
      error?.response?.data?.message?.error || error?.response?.data?.error;
    if (!message) return "An unexpected error occurred";
    let errorMessage = "";
    Array.isArray(message)
      ? message?.map(
          (item, i) => (errorMessage = `${errorMessage} • ${item} \n`)
        )
      : (errorMessage = message);
    return errorMessage;
  };

  // Function to handle API requests
  const handleRequest = async ({
    method,
    route,
    data,
    headers,
    showAlert = true,
    isFormData,
  }) => {
    const url = BaseURL(route);
    const _headers = {
      Accept: "application/json",
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
      timezone: momentTimezone.tz.guess(),
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      ...headers,
    };

    try {
      const response = await axios({ method, url, data, headers: _headers });
      return { response: response?.data, error: null };
    } catch (error) {
      // // Handle 401 errors (unauthorized) - attempt to refresh token
      // if (error?.response?.status === 401 && refreshToken) {
      //   try {
      //     const newAccessToken = await refreshAccessToken();
      //     if (newAccessToken) {
      //       // Retry the request with the new token
      //       const retryHeaders = {
      //         ..._headers,
      //         Authorization: `Bearer ${newAccessToken}`,
      //       };
      //       const retryResponse = await axios({
      //         method,
      //         url,
      //         data,
      //         headers: retryHeaders,
      //       });
      //       return { response: retryResponse?.data, error: null };
      //     }
      //   } catch (refreshError) {
      //     // If token refresh or retry fails, handle the error
      //     handleLogout();
      //   }
      // }

      const errorMessage = getErrorMsg(error);
      if (showAlert) {
        RenderToast({
          type: "error",
          message: errorMessage,
        });
      }
      // if (error?.response?.status === 401) {
      //   handleLogout();
      // }

      return { error, response: null, message: errorMessage };
    }
  };

  return {
    Get: ({ route, headers = {}, showAlert = true }) =>
      handleRequest({ method: "get", route, headers, showAlert }),

    Post: ({
      route,
      data = {},
      headers = {},
      showAlert = true,
      isFormData = false,
    }) =>
      handleRequest({
        method: "post",
        route,
        data,
        headers,
        showAlert,
        isFormData,
      }),

    Put: ({
      route,
      data = {},
      headers = {},
      showAlert = true,
      isFormData = false,
    }) =>
      handleRequest({
        method: "put",
        route,
        data,
        headers,
        showAlert,
        isFormData,
      }),

    Patch: ({
      route,
      data = {},
      headers = {},
      showAlert = true,
      isFormData = false,
    }) =>
      handleRequest({
        method: "patch",
        route,
        data,
        headers,
        showAlert,
        isFormData,
      }),

    Delete: ({ route, headers = {}, showAlert = true }) =>
      handleRequest({ method: "delete", route, headers, showAlert }),
  };
};

export default useAxios;
